import Loading from '../../../components/Loading/Loading';
import React from 'react';
import { AntDesign, FontAwesome, MaterialCommunityIcons } from '@expo/vector-icons';
import { COLORS, ERROR_COLOR, NEUTRAL_TEXT0, WARNING_COLOR } from '../../../config/designSystem/Colors';
import { FAQ } from '../../../components/FAQ/FAQ';
import { FC } from 'react';
import { IFeature } from '../../../state/stores/Feature/FeatureModel';
import { ISurvey } from '../../../state/stores/Survey/SurveyModel';
import { MobileH1, ParagraphLRegular, ParagraphMRegular } from '../../../config/designSystem/typography';
import { ScrollView, View } from 'react-native';
import { SessionPresentationProps } from '../config';
import { observer } from 'mobx-react';
import { useBreakpointStyles } from '../../../hooks/useBreakpointStyles';
import { useBreakpointWidth } from '../../../hooks/useBreakpointWidth';

export interface SurveyResultData extends Record<string, unknown> {
  feature?: IFeature;
  survey?: ISurvey;
  fromSurvey?: boolean;
  pathId?: string | null;
  sessionId?: string;
  frame?: any;
}

export interface PresentSurveyResultProps extends SessionPresentationProps<SurveyResultData> {
  labels?: {
    LOADING?: string;
  }
  data: SurveyResultData;
}

const _PresentSurveyResult: FC<PresentSurveyResultProps> = ({
  labels,
  data,
}) => {
  const feature = data?.feature;
  const survey = data?.survey;
  const fromSurvey = data?.fromSurvey;

  const styles = useBreakpointStyles({
    container: {
      flex: 1,
      xl: {
        maxWidth: '70%',
        alignSelf: 'center',
      },
    },
    scrollContainer: {
      justifyContent: 'center',
      alignItems: 'center',
    },
    card: {
      backgroundColor: 'white',
      borderRadius: 20,
      padding: 20,
      marginTop: 30,
    },
    resultContainer: {
      alignItems: 'center',
      justifyContent: 'center',
      borderBottomColor: 'black',
      alignSelf: 'center',
    },
    outputSentence: {
      alignItems: 'center',
    },
    circleContainer: {
      marginTop: 20,
      width: 50,
      height: 50,
      borderRadius: 25,
      alignItems: 'center',
      justifyContent: 'center',
      xl: {
        width: 75,
        height: 75,
        borderRadius: 37.5,
      }
    },
    titleContainer: {
      marginTop: 10,
      alignItems: 'center',
    },
    descriptionContainer: {
      marginTop: 20,
      lg: {
        width: 400,
      }
    },
  });

  const SIZE_LOGO = useBreakpointWidth({
    sm: 25,
    xl: 50,
  });

  const colorResult = () => {
    if (feature?.logo === 'NORMAL') {
      return COLORS.state.success.primary;
    } else if (feature?.logo === 'WARNING') {
      return WARNING_COLOR;
    } else if (feature?.logo === 'ALERT') {
      return ERROR_COLOR;
    } else {
      return COLORS.state.info.primary;
    }
  };

  const mainColor = colorResult();

  const renderLogo = () => {
    if (feature?.logo === 'NORMAL') {
      return <FontAwesome name='thumbs-up' color={NEUTRAL_TEXT0} size={SIZE_LOGO} />;
    }
    if (feature?.logo === 'WARNING') {
      return <AntDesign name='warning' color={NEUTRAL_TEXT0} size={SIZE_LOGO} />;
    }
    if (feature?.logo === 'ALERT') {
      return <MaterialCommunityIcons name='lightning-bolt' color={NEUTRAL_TEXT0} size={SIZE_LOGO} />;
    }
    return null;
  };

  if (!feature) {
    return (
      <Loading withLabel={true} label={labels?.LOADING} />
    );
  }

  return (
    <View style={styles.container}>
      <ScrollView
        contentContainerStyle={styles.scrollContainer}
        style={styles.container}
      >
        <View style={styles.card}>
          <View style={styles.resultContainer}>
            <View style={styles.outputSentence}>
              <ParagraphLRegular>
                {feature?.outputSentence}
              </ParagraphLRegular>
            </View>
            <View style={[styles.circleContainer, { backgroundColor: mainColor }]}>
              {renderLogo()}
            </View>
            <View style={styles.titleContainer}>
              <MobileH1 color={mainColor}>
                {feature?.name}
              </MobileH1>
            </View>
          </View>
          <View style={styles.descriptionContainer}>
            <ParagraphMRegular>
              {fromSurvey ? feature?.descriptionForSurvey : feature?.description}
            </ParagraphMRegular>
          </View>
        </View>
        {survey?.displayInfo && (
          <FAQ
            title={survey.shortDescription}
            text={survey.longDescription}
            extraInfo={{ title: survey?.title }}
            backgroundColor={COLORS.surface.secondary}
            backgroundColorText={mainColor}
            opacity={1}
            titleTextColor='white'
            textColor='black'
          />
        )}
      </ScrollView>
    </View>
  );
};

export const PresentSurveyResult = observer(_PresentSurveyResult);
